const STORAGE_KEY = 'velvera-attribution-v1';
const ATTRIBUTION_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'];

function sanitizeTagValue(value) {
  return value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 40);
}

function normalizeAttribution(input) {
  const normalized = {};

  for (const key of ATTRIBUTION_KEYS) {
    const value = typeof input[key] === 'string' ? sanitizeTagValue(input[key]) : '';
    if (value) normalized[key] = value;
  }

  if (typeof input.referrer_host === 'string') {
    const referrerHost = sanitizeTagValue(input.referrer_host);
    if (referrerHost) normalized.referrer_host = referrerHost;
  }

  return normalized;
}

function readStoredAttribution() {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    return normalizeAttribution(JSON.parse(raw));
  } catch {
    return {};
  }
}

function readCurrentAttribution() {
  const url = new URL(window.location.href);
  const current = {};

  for (const key of ATTRIBUTION_KEYS) {
    const value = url.searchParams.get(key);
    if (value) current[key] = value;
  }

  if (!current.utm_source && document.referrer) {
    try {
      const referrerUrl = new URL(document.referrer);
      if (referrerUrl.host && referrerUrl.host !== window.location.host) {
        current.referrer_host = referrerUrl.host;
      }
    } catch {
      // Ignore malformed referrers.
    }
  }

  return normalizeAttribution(current);
}

function persistAttribution(attribution) {
  if (!Object.keys(attribution).length) return;

  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(attribution));
  } catch {
    // Ignore storage failures.
  }
}

function getEffectiveAttribution() {
  const stored = readStoredAttribution();
  const current = readCurrentAttribution();

  if (Object.keys(current).length) {
    const merged = { ...stored, ...current };
    persistAttribution(merged);
    return merged;
  }

  return stored;
}

function buildAttributionTags(attribution) {
  const tags = [];

  for (const key of ATTRIBUTION_KEYS) {
    if (attribution[key]) tags.push(`${key}_${attribution[key]}`);
  }

  if (attribution.referrer_host) {
    tags.push(`referrer_${attribution.referrer_host}`);
  }

  return tags;
}

function getEntryRouteTag(pathname = window.location.pathname) {
  const routeParts = pathname
    .split('/')
    .filter(Boolean)
    .map((part) => sanitizeTagValue(part))
    .filter(Boolean);

  if (!routeParts.length) return 'entry_home';
  if (routeParts[0] === 'products' && routeParts[1]) return `entry_product_${routeParts[1]}`;
  if (routeParts[0] === 'collections' && routeParts[1]) return `entry_collection_${routeParts[1]}`;
  if (routeParts[0] === 'pages' && routeParts[1]) return `entry_page_${routeParts[1]}`;
  if (routeParts[0] === 'blogs' && routeParts[1] && routeParts[2]) return `entry_article_${routeParts[2]}`;
  if (routeParts[0] === 'blogs' && routeParts[1]) return `entry_blog_${routeParts[1]}`;

  return `entry_route_${routeParts.join('_')}`;
}

function getPageContextTag() {
  const template = document.querySelector('#MainContent')?.dataset.template ?? '';
  const pathname = window.location.pathname;

  if (template.includes('product') || pathname.startsWith('/products/')) return 'landing_product';
  if (template.includes('collection') || pathname.startsWith('/collections/')) return 'landing_collection';
  if (template.includes('search') || pathname.startsWith('/search')) return 'landing_search';
  if (template.includes('article') || template.includes('blog') || pathname.startsWith('/blogs/')) return 'landing_content';
  if (template.includes('page.quiz') || pathname.includes('find-my-perfect-match') || pathname.includes('/quiz')) return 'landing_quiz';
  if (template.includes('contact') || pathname.startsWith('/pages/contact')) return 'landing_contact';
  if (template.includes('index') || pathname === '/') return 'landing_homepage';
  if (template.includes('page') || pathname.startsWith('/pages/')) return 'landing_page';

  return '';
}

function getFormContextTags(form) {
  const tags = [];
  const customContext = form.querySelector('input[name="contact[signup_context]"]')?.value;

  if (customContext) {
    const sanitizedContext = sanitizeTagValue(customContext);
    if (sanitizedContext) tags.push(`capture_${sanitizedContext}`);
  }

  if (form.closest('.shopify-section-footer, footer, #shopify-section-footer-group')) {
    tags.push('form_footer');
  }

  if (form.closest('#MainContent')) {
    const pageTag = getPageContextTag();

    if (pageTag === 'landing_homepage') tags.push('form_homepage_capture');
    if (pageTag === 'landing_product') tags.push('form_product_capture');
    if (pageTag === 'landing_collection') tags.push('form_collection_capture');
    if (pageTag === 'landing_quiz') tags.push('form_quiz_capture');
  }

  if (form.closest('.sticky-add-to-cart')) {
    tags.push('form_sticky_add_to_cart');
  }

  return tags;
}

function mergeTags(baseTags, attributionTags) {
  const merged = [...baseTags, ...attributionTags].filter(Boolean);
  return [...new Set(merged)];
}

function inheritUtmParamsIntoLink(link) {
  if (!(link instanceof HTMLAnchorElement)) return;

  const href = link.getAttribute('href');
  if (!href || href.startsWith('#') || href.startsWith('mailto:') || href.startsWith('tel:')) return;

  const currentUrl = new URL(window.location.href);
  const supportUrl = new URL(href, currentUrl);

  if (supportUrl.origin !== currentUrl.origin) return;

  for (const paramName of ATTRIBUTION_KEYS) {
    const currentValue = currentUrl.searchParams.get(paramName);
    if (currentValue && !supportUrl.searchParams.get(paramName)) {
      supportUrl.searchParams.set(paramName, currentValue);
    }
  }

  link.setAttribute('href', `${supportUrl.pathname}${supportUrl.search}${supportUrl.hash}`);
}

function prepareSignupForm(form) {
  if (!(form instanceof HTMLFormElement)) return;
  if (!form.querySelector('input[name="contact[email]"]')) return;

  const attribution = getEffectiveAttribution();
  const attributionTags = buildAttributionTags(attribution);
  const contextTags = [getPageContextTag(), getEntryRouteTag(), ...getFormContextTags(form)].filter(Boolean);
  if (!attributionTags.length && !contextTags.length) return;

  let tagField = form.querySelector('input[name="contact[tags]"]');

  if (!tagField) {
    tagField = document.createElement('input');
    tagField.type = 'hidden';
    tagField.name = 'contact[tags]';
    form.prepend(tagField);
  }

  const baseTags = tagField.dataset.baseTags ?? tagField.value ?? '';
  tagField.dataset.baseTags = baseTags;

  const baseTagList = baseTags
    .split(',')
    .map((tag) => tag.trim())
    .filter(Boolean);

  tagField.value = mergeTags(baseTagList, [...contextTags, ...attributionTags]).join(', ');
}

function prepareSignupForms(root = document) {
  const forms = root.querySelectorAll('form');
  forms.forEach((form) => prepareSignupForm(form));
}

function getSignupSupportLinks(root = document) {
  const links = new Set(root.querySelectorAll('.email-signup__support-link'));

  root.querySelectorAll('form').forEach((form) => {
    if (!(form instanceof HTMLFormElement)) return;
    if (!form.querySelector('input[name="contact[email]"]')) return;
    form.querySelectorAll('a[href]').forEach((link) => links.add(link));
  });

  return [...links];
}

function getTrackedCtaLinks(root = document) {
  const selectors = [
    '.sticky-add-to-cart__support-link',
    '.velvera-home-faq a',
    '.velvera-home-method-guide a',
    '.velvera-social-proof a',
    '.velvera-shade-guide a',
    '.velvera-search-guidance a',
    '.velvera-search-intent-panel a',
    '.velvera-search-recovery a',
    '.velvera-product-review-paths a',
    '.velvera-product-decision-strip a',
    '.velvera-product-match__actions a',
    '.velvera-product-selection-summary__actions a',
    '.velvera-product-support-steps a',
    '.velvera-lead-expectations a',
  ];
  const links = new Set();

  selectors.forEach((selector) => {
    root.querySelectorAll(selector).forEach((link) => links.add(link));
  });

  return [...links];
}

function prepareSupportLinks(root = document) {
  const links = new Set([...getSignupSupportLinks(root), ...getTrackedCtaLinks(root)]);
  links.forEach((link) => inheritUtmParamsIntoLink(link));
}

function initUtmAttribution() {
  getEffectiveAttribution();
  prepareSignupForms();
  prepareSupportLinks();

  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      mutation.addedNodes.forEach((node) => {
        if (!(node instanceof HTMLElement)) return;
        if (node.matches?.('form')) prepareSignupForm(node);
        if (node.matches?.('.email-signup__support-link')) inheritUtmParamsIntoLink(node);
        prepareSignupForms(node);
        prepareSupportLinks(node);
      });
    }
  });

  observer.observe(document.body, { childList: true, subtree: true });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initUtmAttribution, { once: true });
} else {
  initUtmAttribution();
}
